<template>
  <div class="page-shell">
    <div class="page-glow page-glow--one"></div>
    <div class="page-glow page-glow--two"></div>

    <header class="topbar">
      <div class="topbar-inner">
        <div class="brand-block">
          <img class="brand-logo" :src="brandLogo" alt="太一排盘" />
          <div class="brand-copy">
            <div class="brand-name">太一排盘 · 风水</div>
            <div class="brand-note">九宫飞星 · 八宅命卦 · 紫白诀流月速查</div>
          </div>
        </div>

        <nav class="station-nav" aria-label="太一站群">
          <template v-for="link in stationLinks" :key="link.key">
            <span v-if="link.current" class="station-link station-link--current">{{ link.label }}</span>
            <a
              v-else
              class="station-link"
              :href="link.href"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ link.label }}
            </a>
          </template>
        </nav>
      </div>
    </header>

    <main class="page-main">
      <div class="container">
        <div class="tabs-nav" aria-label="风水工具切换">
          <button
            v-for="tool in toolTabs"
            :key="tool.key"
            type="button"
            :class="['tab', { active: currentTab === tool.key }]"
            @click="switchTab(tool.key)"
          >
            <span class="tab-label">{{ tool.label }}</span>
            <span class="tab-note">{{ tool.note }}</span>
          </button>
        </div>

        <!-- 流年流月飞星 Tab -->
        <section v-if="currentTab === 'liunian'" class="tool-view">
      <div class="header">
        <h1>流年九宫飞星排盘</h1>
        <div class="control-panel">
          <div class="control-group">
            <span class="control-label">公元年份</span>
            <div class="stepper">
              <button type="button" class="stepper-btn" aria-label="上一年" @click="adjustTargetYear(-1)">
                <svg class="inline-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                </svg>
              </button>
              <input
                id="year"
                type="number"
                class="stepper-value"
                inputmode="numeric"
                :value="targetYear"
                @input="setTargetYear($event.target.value)"
              />
              <button type="button" class="stepper-btn" aria-label="下一年" @click="adjustTargetYear(1)">
                <svg class="inline-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="control-group control-group--select">
            <span class="control-label">飞星周期</span>
            <div class="select-wrap">
              <select v-model="targetMonth" class="month-select">
                <option :value="0">全年</option>
                <option v-for="m in 12" :key="m" :value="m">{{m}}月飞星</option>
              </select>
            </div>
          </div>
        </div>
        <p class="summary">
          入中宫飞星：<strong>{{ liunianCenterData.name }}</strong>
          <span v-if="targetMonth > 0" class="sub-text">({{ targetMonth }}月流月盘)</span>
        </p>
      </div>

      <div class="grid-container">
        <div 
          v-for="(cell, index) in liunianGrid" 
          :key="index" 
          class="grid-cell"
          :class="getStarColorClass(cell.type)"
        >
          <div class="cell-palace-tags" v-if="cell.palaceTags">{{ cell.palaceTags }}</div>
          <div class="cell-bagua">{{ cell.direction }} {{ cell.bagua }}{{ cell.palace }}</div>
          <div class="cell-title">{{ cell.starTitle }}</div>
          <div class="cell-main-wrap">
            <div class="cell-star-num">{{ cell.starNum }}</div>
            <div class="cell-star-name">{{ cell.starName }}</div>
          </div>
          <div class="cell-star-tags">{{ cell.starTags }}</div>
        </div>
      </div>

      <!-- 流年排盘防伪水印 (紧贴排盘，方便截图) -->
      <div class="watermark-box">
        <span class="watermark-logo">太一排盘-甲午子</span>
      </div>

      <div class="advice-section">
        <h3>🏡 简要化解建议 ({{ targetYear }}年)</h3>
        <ul class="advice-list">
          <li><span class="warning">五黄廉贞土星 (五黄煞·大凶)</span> 飞临 <strong>{{ getDirectionByStar(5, liunianGrid) }}</strong>，为主灾祸、破财。<strong>【化解】</strong>宜静不宜动，不可动土。宜挂金属性六帝钱或纯铜铃铛泄土煞，忌见红黄。</li>
          <li><span class="warning">二黑巨门土星 (病符位·大凶)</span> 飞临 <strong>{{ getDirectionByStar(2, liunianGrid) }}</strong>，主疾病、伤痛、脾胃。<strong>【化解】</strong>保持清洁明亮。宜放开盖铜葫芦化泄病气。</li>
          <li><span class="warning">三碧禄存木星 (是非位·凶)</span> 飞临 <strong>{{ getDirectionByStar(3, liunianGrid) }}</strong>，主官非、小人。<strong>【化解】</strong>宜在此放红色地毯或长明红灯化是非，忌摆水植。</li>
          <li><span class="warning">七赤破军金星 (破财位·凶)</span> 飞临 <strong>{{ getDirectionByStar(7, liunianGrid) }}</strong>，主破财、盗贼。<strong>【化解】</strong>宜放黑化蓝地毯或一杯清水（金生水泄气）化杀气。</li>
          <li><span class="lucky">九紫右弼火星 (喜神位·大吉)</span> 飞临 <strong>{{ getDirectionByStar(9, liunianGrid) }}</strong>，九运大旺星！主添丁、升职。<strong>【催旺】</strong>宜放红花绿叶木制大叶植物催旺运势。</li>
          <li><span class="lucky">一白贪狼水星 (桃花位·吉)</span> 飞临 <strong>{{ getDirectionByStar(1, liunianGrid) }}</strong>，主人缘、偏财。<strong>【催旺】</strong>宜放金属活水摆件催横财，鲜花催缘分。</li>
          <li><span class="lucky">四绿文曲木星 (文昌位·吉)</span> 飞临 <strong>{{ getDirectionByStar(4, liunianGrid) }}</strong>，主文章考学。<strong>【催旺】</strong>最宜放书桌，供文昌塔或四枝水养富贵竹助学业。</li>
          <li><span class="lucky">六白武曲金星 (偏财位·吉)</span> 飞临 <strong>{{ getDirectionByStar(6, liunianGrid) }}</strong>，主武职、横财、贵人。<strong>【催旺】</strong>放置聚宝盆、黄色地毯催偏财及贵人扶持。</li>
          <li><span class="lucky">八白左辅土星 (正财位·吉)</span> 飞临 <strong>{{ getDirectionByStar(8, liunianGrid) }}</strong>，传统第一正财星。<strong>【催旺】</strong>宜明亮开阔，可摆水晶洞、陶瓷存钱罐催旺正财。</li>
        </ul>
      </div>
        </section>

        <!-- 紫白诀 Tab -->
        <section v-if="currentTab === 'zibaijue'" class="tool-view">
      <div class="header">
        <h1>紫白诀流月飞星速查</h1>
        <div class="control-panel control-panel--single">
          <div class="control-group">
            <span class="control-label">公元年份</span>
            <div class="stepper">
              <button type="button" class="stepper-btn" aria-label="上一年" @click="adjustZbYear(-1)">
                <svg class="inline-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                </svg>
              </button>
              <input
                id="zbYear"
                type="number"
                class="stepper-value"
                inputmode="numeric"
                :value="zbYear"
                @input="setZbYear($event.target.value)"
              />
              <button type="button" class="stepper-btn" aria-label="下一年" @click="adjustZbYear(1)">
                <svg class="inline-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <p class="summary">
          {{ zbYear }}年如月即运，寿星为：<strong class="lucky">紫白{{ zbCenterItem ? zbCenterItem.star.name : '' }}入中兮</strong>
        </p>
      </div>

      <!-- 12个月份卡片速查表 -->
      <div class="zb-grid">
        <div 
          v-for="item in zibaijueList" 
          :key="item.month" 
          class="zb-card"
          :class="getStarColorClass(item.star.type)"
        >
          <div class="zb-month">{{ item.monthName }}</div>
          <div class="zb-star-num">{{ item.star.num }}</div>
          <div class="zb-star-name">{{ item.star.name }}</div>
          <div class="zb-label" :class="item.labelClass">《{{ item.label }}》</div>
          <div class="zb-direction">旺：{{ item.wangDir }}</div>
        </div>
      </div>

      <!-- 紫白诀实战指南（根据年份实时生成） -->
      <div class="advice-section">
        <h3>✨ {{ zbYear }}年紫白诀实战指南</h3>
        <div class="distinction-note">
          <p><strong>《紫白诀》以一白、六白、八白、九紫为“紫白吉星”，宜趁吉月展开重要事务，凶月宜静守谨慎。</strong></p>
        </div>
        <ul class="advice-list">
          <li v-for="entry in zbAdviceList" :key="entry.starNum">
            <span :class="entry.isLucky ? 'lucky' : 'warning'">{{ entry.starName }}（{{ entry.level }}）</span>
            {{ zbYear }}年 <strong>{{ entry.months }}</strong> 将入中宫：{{ entry.desc }}
          </li>
        </ul>
        <p class="sub-hint" style="margin-top:12px">（注：以农历寘月为岁首，各月对应公历时间约偏差10-45天，以节气入月为准。）</p>
      </div>

      <!-- 紫白诀防伪水印 -->
      <div class="watermark-box">
        <span class="watermark-logo">太一排盘-甲午子</span>
      </div>
        </section>

        <!-- 本命八宅 Tab -->
        <section v-if="currentTab === 'bazhai'" class="tool-view">
      <div class="header">
        <h1>本命八宅排盘</h1>
        
        <!-- 子模式切换开关 -->
        <div class="sub-tabs">
          <div class="sub-tab" :class="{active: bazhaiMode === 'ming'}" @click="bazhaiMode = 'ming'">
            <svg class="sub-tab-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
              <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
            </svg>
            <span>看个人命卦</span>
          </div>
          <div class="sub-tab" :class="{active: bazhaiMode === 'zhai'}" @click="bazhaiMode = 'zhai'">
            <svg class="sub-tab-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4.5 10.5 12 4l7.5 6.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
              <path d="M6.5 9.5v9h11v-9" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
              <path d="M10 18.5v-5h4v5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
            </svg>
            <span>看房屋宅卦</span>
          </div>
        </div>

        <template v-if="bazhaiMode === 'ming'">
          <div class="control-panel control-panel--compact">
            <div class="control-group">
              <span class="control-label">出生年份</span>
              <div class="stepper stepper--compact">
                <button type="button" class="stepper-btn" aria-label="出生年份减一年" @click="adjustBirthYear(-1)">
                  <svg class="inline-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                  </svg>
                </button>
                <input
                  type="number"
                  class="stepper-value stepper-value--compact"
                  inputmode="numeric"
                  :value="birthYear"
                  @input="setBirthYear($event.target.value)"
                />
                <button type="button" class="stepper-btn" aria-label="出生年份加一年" @click="adjustBirthYear(1)">
                  <svg class="inline-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="control-group control-group--select">
              <span class="control-label">性别</span>
              <div class="select-wrap select-wrap--compact">
                <select v-model="gender" class="month-select">
                  <option value="male">男</option>
                  <option value="female">女</option>
                </select>
              </div>
            </div>
          </div>
          <p class="summary">
            本命卦为：<strong>{{ mingGuaInfo.name }}</strong> ({{ mingGuaInfo.group }}) 
          </p>
        </template>

        <template v-if="bazhaiMode === 'zhai'">
          <div class="control-panel control-panel--single">
            <div class="control-group control-group--select control-group--wide">
              <span class="control-label">房屋坐向</span>
              <div class="select-wrap select-wrap--wide">
                <select v-model="houseSitting" class="month-select">
                   <option :value="1">坎宅 (坐北朝南)</option>
                   <option :value="8">艮宅 (坐东北朝西南)</option>
                   <option :value="3">震宅 (坐东朝西)</option>
                   <option :value="4">巽宅 (坐东南朝西北)</option>
                   <option :value="9">离宅 (坐南朝北)</option>
                   <option :value="2">坤宅 (坐西南朝东北)</option>
                   <option :value="7">兑宅 (坐西朝东)</option>
                   <option :value="6">乾宅 (坐西北朝东南)</option>
                </select>
              </div>
            </div>
          </div>
          <p class="summary">
            房屋宅卦为：<strong>{{ zhaiGuaInfo.name }}</strong> ({{ zhaiGuaInfo.group }})
          </p>
        </template>
      </div>

      <div class="grid-container">
        <div 
          v-for="(cell, index) in bazhaiGrid" 
          :key="index" 
          class="grid-cell"
          :class="getStarColorClass(cell.type)"
        >
          <div class="cell-palace-tags" v-if="cell.palaceTags">{{ cell.palaceTags }}</div>
          <div class="cell-bagua">{{ cell.direction }} {{ cell.bagua }}{{ cell.palace }}</div>
          <div class="cell-title">{{ cell.starTitle }}</div>
          <div class="cell-main-wrap">
            <div class="cell-star-num" :style="cell.isCenter ? 'font-size: 1.8rem' : 'font-size: 1.4rem'">{{ cell.starNum }}</div>
            <div class="cell-star-name" v-if="cell.starName">{{ cell.starName }}</div>
          </div>
          <div class="cell-star-tags" style="font-weight: bold">{{ cell.starTags }}</div>
        </div>
      </div>
      
      <!-- 八宅排盘防伪水印 (紧贴排盘，方便截图) -->
      <div class="watermark-box">
        <span class="watermark-logo">太一排盘-甲午子</span>
      </div>

      <div class="advice-section">
        <h3>☯ 房屋四吉四凶方位指南</h3>
        <div class="distinction-note">
          <p><strong>💡【实战指引】命卦与宅卦的区别：</strong></p>
          <p>• <strong>看个人命卦</strong>：定“内局”。每个人有属于自己一生的吉凶方位（如东四命或西四命），主要用于寻找适合自己的<strong>卧室安床、办公桌坐向及灶向</strong>。</p>
          <p>• <strong>看房屋宅卦</strong>：定“外局”。房屋本身的吉凶气场分布，主要用于决定整套房子的<strong>大门朝向、主卧室位置、厨房及卫生间方位</strong>。</p>
          <p class="sub-hint">（注：风水云“以门为向看宅，以命配卦看床”。两者若出现冲突，大局以宅为主，个人起居以命为主。）</p>
        </div>
        <ul class="advice-list">
          <li><span class="lucky">生气方 (第一大吉)</span>：主旺财、催丁、事业顺遂升职。宜开大门、安床做主卧。</li>
          <li><span class="lucky">延年方 (次吉)</span>：主和睦、婚姻、延寿、贵人。宜安床、作厨房或办公室，利家宅和睦。</li>
          <li><span class="lucky">天医方 (中吉)</span>：主健康、祛病、解灾、意外之财。宜作长辈房卧，可保家人平安健康。</li>
          <li><span class="lucky">伏位方 (小吉)</span>：主平稳、蓄锐、无过无患。适宜做小孩房或安静书房以防打扰。</li>
          <li><span class="warning">绝命方 (第一大凶)</span>：主意外、疾病、退财、血光。此方忌开门、安床，宜作厕所或储物间压煞。</li>
          <li><span class="warning">五鬼方 (大凶)</span>：主火灾、小人、口舌是非、破财。忌作厨房或卧室，宜作卫浴压制。</li>
          <li><span class="warning">六煞方 (次凶)</span>：主烂桃花、官非、散财、遇小人。不宜开门或做主卧，只宜暂存废弃物。</li>
          <li><span class="warning">祸害方 (小凶)</span>：主争执、疲倦、小病、容易遭人陷害。宜静不宜动。</li>
        </ul>
      </div>
        </section>
      </div>
    </main>

    <footer class="site-footer">
      <div class="site-footer-inner">
        <div class="footer-quote">万术归宗 · 太一风水排盘</div>
        <div class="footer-meta">
          <div>Email: <a :href="`mailto:${site.supportEmail}`">{{ site.supportEmail }}</a></div>
          <div v-if="site.isCn" class="site-records">
            <a :href="site.records.icpUrl" target="_blank" rel="noopener noreferrer">{{ site.records.icp }}</a>
            <a :href="site.records.publicUrl" target="_blank" rel="noopener noreferrer">{{ site.records.public }}</a>
          </div>
          <div>&copy; {{ currentYear }} {{ site.hostLabel }} · 弘扬传统文化</div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { siteConfig } from './config/site';

export default {
  name: 'App',
  data() {
    return {
      brandLogo: `${process.env.BASE_URL}logo.png`,
      currentYear: new Date().getFullYear(),
      site: siteConfig,
      stationLinks: siteConfig.stationLinks,
      toolTabs: [
        {
          key: 'liunian',
          label: '九宫流年流月飞星',
          note: '看年度与流月飞星落宫',
          title: '流年流月九宫飞星排盘 - 太一排盘'
        },
        {
          key: 'bazhai',
          label: '八宅排盘',
          note: '看个人命卦与房屋宅卦',
          title: '本命八宅排盘 - 太一排盘'
        },
        {
          key: 'zibaijue',
          label: '紫白诀',
          note: '看流月吉凶与行动节奏',
          title: '紫白诀流月飞星速查 - 太一排盘'
        }
      ],
      currentTab: 'liunian',
      targetYear: new Date().getFullYear(),
      targetMonth: 0,
      zbYear: new Date().getFullYear(), // 紫白诀年份
      bazhaiMode: 'ming',
      birthYear: 1990,
      gender: 'male',
      houseSitting: 1,
      // 流年飞星字典（含 num 字段供紫白诀复用）
      starDict: {
        1: { num: 1, name: '一白水星', title: '桃花位', tags: '人缘 感情 偏财', type: 'water-1' },
        2: { num: 2, name: '二黑土星', title: '病符位', tags: '疾病 伤痛 损丁', type: 'earth-2' },
        3: { num: 3, name: '三碧木星', title: '是非位', tags: '是非 官灾 小人', type: 'wood-3' },
        4: { num: 4, name: '四绿木星', title: '文昌位', tags: '事业 学业 官位', type: 'wood-4' },
        5: { num: 5, name: '五黄土星', title: '五黄煞', tags: '意外 伤害 破财', type: 'earth-5' },
        6: { num: 6, name: '六白金星', title: '偏财位', tags: '武职 贵人 横财', type: 'metal-6' },
        7: { num: 7, name: '七赤金星', title: '破财位', tags: '牢狱 破财 盗窃', type: 'metal-7' },
        8: { num: 8, name: '八白土星', title: '正财位', tags: '财富 财运 置业', type: 'earth-8' },
        9: { num: 9, name: '九紫火星', title: '喜神位', tags: '姻缘 感情 添丁', type: 'fire-9' }
      },
      // 本命八宅字典（完美复用九星颜色体系保障视觉统一）
      bazhaiDict: {
        '生气': { name: '贪狼木', tags: '事业 财富 催丁', type: 'wood-4', num: '大吉' },
        '延年': { name: '武曲金', tags: '婚姻 感情 贵人', type: 'fire-9', num: '次吉' },
        '天医': { name: '巨门土', tags: '健康 解灾 财运', type: 'wood-3', num: '中吉' },
        '伏位': { name: '辅弼木', tags: '平稳 发展 蓄锐', type: 'water-1', num: '小吉' },
        '绝命': { name: '破军金', tags: '疾病 耗财 意外', type: 'earth-5', num: '大凶' },
        '五鬼': { name: '廉贞火', tags: '口舌 破财 小人', type: 'metal-7', num: '大凶' },
        '六煞': { name: '文曲水', tags: '桃花 官非 散财', type: 'metal-6', num: '次凶' },
        '祸害': { name: '禄存土', tags: '争执 疲倦 疾病', type: 'earth-2', num: '小凶' }
      },
      // 基础宫位框架
      renderOrderBase: [
        { palace: 4, direction: '东南', bagua: '巽', palaceTags: '长女 肝胆 手脚' },
        { palace: 9, direction: '正南', bagua: '离', palaceTags: '中女 头 心脏' },
        { palace: 2, direction: '西南', bagua: '坤', palaceTags: '母 胃腹' },
        { palace: 3, direction: '正东', bagua: '震', palaceTags: '长子 肝胆 手脚' },
        { palace: 5, direction: '中宫', bagua: '中', palaceTags: '\u00A0' },
        { palace: 7, direction: '正西', bagua: '兑', palaceTags: '少女 肺 喉鼻' },
        { palace: 8, direction: '东北', bagua: '艮', palaceTags: '幼男 脾 骨' },
        { palace: 1, direction: '正北', bagua: '坎', palaceTags: '中男 肠胃 耳朵' },
        { palace: 6, direction: '西北', bagua: '乾', palaceTags: '父 肺 大肠' }
      ],
      // 紫白诀月份元数据（中文月名，12个月）
      zbMonths: [
        { month: 1,  monthName: '一月 (丑月)'   },
        { month: 2,  monthName: '二月 (寅月)'   },
        { month: 3,  monthName: '三月 (卯月)'   },
        { month: 4,  monthName: '四月 (辰月)'   },
        { month: 5,  monthName: '五月 (巳月)'   },
        { month: 6,  monthName: '六月 (午月)'   },
        { month: 7,  monthName: '七月 (未月)'   },
        { month: 8,  monthName: '八月 (申月)'   },
        { month: 9,  monthName: '九月 (酉月)'   },
        { month: 10, monthName: '十月 (戌月)'   },
        { month: 11, monthName: '十一月 (亥月)' },
        { month: 12, monthName: '十二月 (子月)' }
      ]
    }
  },
  mounted() {
    this.applyViewFromUrl();
    this.syncViewParam();
  },
  computed: {
    pageTitle() {
      return this.toolTabs.find((tool) => tool.key === this.currentTab)?.title || '太一排盘';
    },
    // ==================
    // 1. 流年流月计算模块
    // ==================
    liunianCenterStarNum() {
      let sum = this.sumDigits(this.targetYear);
      while(sum > 9) sum = this.sumDigits(sum);
      let yStar = 11 - sum;
      if (this.targetYear < 2000) yStar = 10 - sum;
      while (yStar <= 0) yStar += 9;
      while (yStar > 9) yStar -= 9;

      if (!this.targetMonth || this.targetMonth === 0) return yStar;
      
      // 流月入中宫计算
      const branch = this.targetYear % 12;
      let base = 2; // 寅申巳亥
      if ([4, 10, 7, 1].includes(branch)) base = 8; // 子午卯酉
      else if ([8, 2, 5, 11].includes(branch)) base = 5; // 辰戌丑未
      
      let mStar = base - (this.targetMonth - 1);
      while (mStar <= 0) mStar += 9;
      while (mStar > 9) mStar -= 9;
      return mStar;
    },
    liunianCenterData() {
      return this.starDict[this.liunianCenterStarNum];
    },
    liunianGrid() {
      const flightPath = [5, 6, 7, 8, 9, 1, 2, 3, 4];
      const palaceToStar = {};
      let currentStar = this.liunianCenterStarNum;
      for (const palace of flightPath) {
        palaceToStar[palace] = currentStar;
        currentStar = (currentStar % 9) + 1;
      }
      return this.renderOrderBase.map(item => {
        const sNum = palaceToStar[item.palace];
        const sData = this.starDict[sNum];
        return {
          ...item,
          starNum: sNum,
          starName: sData.name,
          starTitle: sData.title,
          starTags: sData.tags,
          type: sData.type
        };
      });
    },

    // ==================
    // 2. 本命八宅计算模块
    // ==================
    mingGuaNum() {
      let sum = this.sumDigits(this.birthYear);
      while(sum > 9) sum = this.sumDigits(sum);
      let num = 0;
      if (this.gender === 'male') {
         num = 11 - sum;
         if (num > 9) num -= 9;
         if (num === 5) num = 2; // 男命五黄寄坤宫
      } else {
         num = 4 + sum;
         if (num > 9) num -= 9;
         if (num === 5) num = 8; // 女命五黄寄艮宫
      }
      return num;
    },
    mingGuaInfo() {
      const names = {1:'坎水', 2:'坤土', 3:'震木', 4:'巽木', 6:'乾金', 7:'兑金', 8:'艮土', 9:'离火'};
      const dict = {
        1: { 4: '生气', 9: '延年', 3: '天医', 1: '伏位', 2: '绝命', 8: '五鬼', 6: '六煞', 7: '祸害' },
        2: { 8: '生气', 6: '延年', 7: '天医', 2: '伏位', 1: '绝命', 4: '五鬼', 9: '六煞', 3: '祸害' },
        3: { 9: '生气', 4: '延年', 1: '天医', 3: '伏位', 7: '绝命', 6: '五鬼', 8: '六煞', 2: '祸害' },
        4: { 1: '生气', 3: '延年', 9: '天医', 4: '伏位', 8: '绝命', 2: '五鬼', 7: '六煞', 6: '祸害' },
        6: { 7: '生气', 2: '延年', 8: '天医', 6: '伏位', 9: '绝命', 3: '五鬼', 1: '六煞', 4: '祸害' },
        7: { 6: '生气', 8: '延年', 2: '天医', 7: '伏位', 3: '绝命', 9: '五鬼', 4: '六煞', 1: '祸害' },
        8: { 2: '生气', 7: '延年', 6: '天医', 8: '伏位', 4: '绝命', 1: '五鬼', 3: '六煞', 9: '祸害' },
        9: { 3: '生气', 1: '延年', 4: '天医', 9: '伏位', 6: '绝命', 7: '五鬼', 2: '六煞', 8: '祸害' }
      };
      const east = [1, 3, 4, 9];
      return { 
        name: names[this.mingGuaNum], 
        group: east.includes(this.mingGuaNum) ? '东四命' : '西四命',
        map: dict[this.mingGuaNum]
      };
    },
    zhaiGuaInfo() {
      if (this.houseSitting === 0) return null;
      const names = {1:'坎水', 2:'坤土', 3:'震木', 4:'巽木', 6:'乾金', 7:'兑金', 8:'艮土', 9:'离火'};
      const dict = {
        1: { 4: '生气', 9: '延年', 3: '天医', 1: '伏位', 2: '绝命', 8: '五鬼', 6: '六煞', 7: '祸害' },
        2: { 8: '生气', 6: '延年', 7: '天医', 2: '伏位', 1: '绝命', 4: '五鬼', 9: '六煞', 3: '祸害' },
        3: { 9: '生气', 4: '延年', 1: '天医', 3: '伏位', 7: '绝命', 6: '五鬼', 8: '六煞', 2: '祸害' },
        4: { 1: '生气', 3: '延年', 9: '天医', 4: '伏位', 8: '绝命', 2: '五鬼', 7: '六煞', 6: '祸害' },
        6: { 7: '生气', 2: '延年', 8: '天医', 6: '伏位', 9: '绝命', 3: '五鬼', 1: '六煞', 4: '祸害' },
        7: { 6: '生气', 8: '延年', 2: '天医', 7: '伏位', 3: '绝命', 9: '五鬼', 4: '六煞', 1: '祸害' },
        8: { 2: '生气', 7: '延年', 6: '天医', 8: '伏位', 4: '绝命', 1: '五鬼', 3: '六煞', 9: '祸害' },
        9: { 3: '生气', 1: '延年', 4: '天医', 9: '伏位', 6: '绝命', 7: '五鬼', 2: '六煞', 8: '祸害' }
      };
      const east = [1, 3, 4, 9];
      return { 
        name: names[this.houseSitting], 
        group: east.includes(this.houseSitting) ? '东四宅' : '西四宅',
        map: dict[this.houseSitting]
      };
    },
    // ==================
    // 3. 紫白诀计算模块
    // ==================
    zibaijueList() {
      // 天干决寘月入中宫展开（选用天干五虎巽翁六白八白運算词）
      const stem = this.zbYear % 10;
      let yinBase; // 寘月（第2宫月）入中宫的飞星
      if ([4, 9].includes(stem))       yinBase = 8; // 甲己年 起寘8
      else if ([5, 0].includes(stem))  yinBase = 5; // 乙庚年 起寘5
      else if ([6, 1].includes(stem))  yinBase = 2; // 丙辛年 起寘2
      else if ([7, 2].includes(stem))  yinBase = 8; // 丁壬年 起寘8
      else                              yinBase = 5; // 戌炈年 起寘5

      // 紫白诀射5层地支月份逆飞：寘月入yinBase, 每月减1 (逻飞)
      // 公历月对应：寘月对应公历大圱2月，以此类推
      // 我们定义数组内系公历月(1-12)对应的飞星
      // 公历月 1=丑, 2=寘, 3=卯, 4=辰, 5=巳, 6=午, 7=未, 8=申, 9=酉, 10=戌, 11=亥, 12=子
      // 寘月是公历月数=2, 所以从2开始

      const labelMap = {
        1: { label: '桃花旺月', labelClass: 'lbl-lucky', wangDir: '北方、东北' },
        2: { label: '宴病凶月', labelClass: 'lbl-danger', wangDir: '采光化华' },
        3: { label: '是非凶月', labelClass: 'lbl-warn',   wangDir: '萬红化敌' },
        4: { label: '文昂吉月', labelClass: 'lbl-lucky', wangDir: '东南、居饮' },
        5: { label: '五黄大凶', labelClass: 'lbl-danger', wangDir: '静守化中' },
        6: { label: '偏财大旺', labelClass: 'lbl-lucky', wangDir: '西北、谈判' },
        7: { label: '破财凶月', labelClass: 'lbl-warn',   wangDir: '采水化浏' },
        8: { label: '正财大旺', labelClass: 'lbl-lucky', wangDir: '东北、开业' },
        9: { label: '喜汌吉月', labelClass: 'lbl-lucky', wangDir: '南方、婚居' }
      };

      return this.zbMonths.map(m => {
        // 公历月 offset: 寘月=2月, offset=0. 卯月=3月, offset=1 ...
        const offset = m.month === 1 ? 11 : m.month - 2; // 屁月（公历一月）是上年五月第12个
        let starNum = yinBase - offset;
        while (starNum <= 0) starNum += 9;
        while (starNum > 9) starNum -= 9;
        const star = this.starDict[starNum];
        const lm = labelMap[starNum];
        return { ...m, star, label: lm.label, labelClass: lm.labelClass, wangDir: lm.wangDir };
      });
    },
    zbCenterItem() {
      return this.zibaijueList.find(it => it.month === 2);
    },
    // 按星聚合全年月份，生成实时化解指南条目
    zbAdviceList() {
      const order = [8, 9, 1, 6, 4, 3, 7, 2, 5];
      const descMap = {
        8: { level: '正财大旺月', isLucky: true,  desc: '正财大旺，宜开业、签合同、动土置业、整修添财位。' },
        9: { level: '喜庆吉月',   isLucky: true,  desc: '喜气临门，宜婚嫁、添丁、升职室庆，激活南方旺气。' },
        1: { level: '桃花吉月',   isLucky: true,  desc: '人缘运旺，宜公关谈判、激活桃花位，女性运势极佳。' },
        6: { level: '偏财旺月',   isLucky: true,  desc: '贵人横财，宜求职升迁、谈判打官司，激活西北偏财位。' },
        4: { level: '文昌吉月',   isLucky: true,  desc: '学业事业旺，宜考试、签约，激活东南文昌位。' },
        3: { level: '是非凶月',   isLucky: false, desc: '口舌是非增多，忌争执诉讼，宜低调，放红色物化气。' },
        7: { level: '破财凶月',   isLucky: false, desc: '破财盗窃风险升，忌轻信投资，宜放清水泏气。' },
        2: { level: '病符凶月',   isLucky: false, desc: '疾病病符月，宜保健防病，摆铜葫芦化泤病气。' },
        5: { level: '五黄大凶月', isLucky: false, desc: '全年最凶月，战对禁止动土装修，摆六帝錢化煞。' }
      };
      const grouped = {};
      this.zibaijueList.forEach(item => {
        const sn = item.star.num;
        if (!grouped[sn]) grouped[sn] = [];
        grouped[sn].push(item.month + '月');
      });
      return order.map(sn => ({
        starNum: sn,
        starName: this.starDict[sn].name,
        months: grouped[sn] ? grouped[sn].join('、') : '无',
        ...descMap[sn]
      }));
    },
    activeBazhaiInfo() {
      return this.bazhaiMode === 'ming' ? this.mingGuaInfo : this.zhaiGuaInfo;
    },
    bazhaiGrid() {
      const gMap = this.activeBazhaiInfo.map;
      
      return this.renderOrderBase.map(item => {
        if (item.palace === 5) {
           return { 
             ...item, 
             isCenter: true, 
             starTitle: this.bazhaiMode === 'ming' ? '本命卦' : '房屋宅卦', 
             starNum: this.activeBazhaiInfo.name, 
             starName: null, 
             starTags: this.activeBazhaiInfo.group, 
             type: 'earth-8' 
           };
        }
        const bzKey = gMap[item.palace];
        const bzData = this.bazhaiDict[bzKey];
        
        return { 
          ...item, 
          isCenter: false, 
          starTitle: bzKey, 
          starNum: bzData.num, 
          starName: bzData.name, 
          starTags: bzData.tags, 
          type: bzData.type 
        };
      });
    }
  },
  watch: {
    currentTab() {
      this.syncViewParam();
    },
    pageTitle: {
      immediate: true,
      handler(val) {
        document.title = val;
      }
    }
  },
  methods: {
    switchTab(key) {
      if (this.currentTab === key) {
        this.syncViewParam();
        return;
      }
      this.currentTab = key;
    },
    getCurrentYear() {
      return new Date().getFullYear();
    },
    sanitizeYear(value, fallback, min = 1900, max = 2100) {
      const parsed = Number.parseInt(String(value).trim(), 10);
      if (Number.isNaN(parsed)) {
        return fallback;
      }
      return Math.min(max, Math.max(min, parsed));
    },
    applyViewFromUrl() {
      const url = new URL(window.location.href);
      const defaultView = this.toolTabs[0]?.key || 'liunian';
      const queryView = url.searchParams.get('view');
      const hashView = url.hash.replace('#', '');
      const isValid = (view) => this.toolTabs.some((tool) => tool.key === view);

      if (isValid(queryView)) {
        this.currentTab = queryView;
        return;
      }

      if (isValid(hashView)) {
        this.currentTab = hashView;
        return;
      }

      this.currentTab = defaultView;
    },
    syncViewParam() {
      const url = new URL(window.location.href);
      const defaultView = this.toolTabs[0]?.key || 'liunian';

      if (this.currentTab === defaultView) {
        url.searchParams.delete('view');
      } else {
        url.searchParams.set('view', this.currentTab);
      }

      url.hash = '';
      const nextUrl = `${url.pathname}${url.search}`;
      const currentUrl = `${window.location.pathname}${window.location.search}`;

      if (nextUrl === currentUrl && !window.location.hash) {
        return;
      }

      window.history.replaceState(null, '', nextUrl);
    },
    setTargetYear(value) {
      const max = Math.max(2100, this.getCurrentYear() + 20);
      this.targetYear = this.sanitizeYear(value, this.targetYear, 1900, max);
    },
    adjustTargetYear(delta) {
      this.setTargetYear(this.targetYear + delta);
    },
    setZbYear(value) {
      const max = Math.max(2100, this.getCurrentYear() + 20);
      this.zbYear = this.sanitizeYear(value, this.zbYear, 1900, max);
    },
    adjustZbYear(delta) {
      this.setZbYear(this.zbYear + delta);
    },
    setBirthYear(value) {
      this.birthYear = this.sanitizeYear(value, this.birthYear, 1900, this.getCurrentYear());
    },
    adjustBirthYear(delta) {
      this.setBirthYear(this.birthYear + delta);
    },
    sumDigits(number) {
        return number.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    },
    getStarColorClass(type) {
      return type ? `star-type-${type}` : '';
    },
    getDirectionByStar(targetStarNum, grid) {
      const cell = grid.find(c => c.starNum === targetStarNum);
      return cell ? cell.direction + ` (${cell.bagua}宫)` : '未知';
    }
  }
}
</script>

<style>
:root {
  --bg-page: #f2eef9;
  --bg-page-soft: #faf8fe;
  --bg-card: rgba(255, 255, 255, 0.82);
  --bg-card-strong: #ffffff;
  --bg-card-muted: #f6f2fd;
  --text-primary: #312f63;
  --text-secondary: #707499;
  --text-muted: #9fa3bf;
  --brand-primary: #7a49e2;
  --brand-primary-soft: #9d7bf0;
  --brand-primary-ghost: rgba(122, 73, 226, 0.08);
  --brand-primary-ghost-strong: rgba(122, 73, 226, 0.16);
  --border-soft: rgba(122, 73, 226, 0.12);
  --border-strong: rgba(122, 73, 226, 0.18);
  --shadow-card: 0 20px 48px rgba(82, 63, 141, 0.1);
  --shadow-soft: 0 12px 28px rgba(82, 63, 141, 0.08);
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  color: var(--text-primary);
  font-family:
    "PingFang SC",
    "Microsoft YaHei",
    "Noto Sans SC",
    "Helvetica Neue",
    Arial,
    sans-serif;
  background:
    radial-gradient(circle at top left, rgba(157, 123, 240, 0.22), transparent 28%),
    radial-gradient(circle at 88% 14%, rgba(122, 73, 226, 0.14), transparent 24%),
    linear-gradient(180deg, #fbf9ff 0%, var(--bg-page) 44%, #f6f2fd 100%);
}

a {
  color: inherit;
  text-decoration: none;
}

button,
input,
select {
  font: inherit;
}

.page-shell {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.page-glow {
  position: fixed;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(8px);
  opacity: 0.72;
}

.page-glow--one {
  top: -120px;
  left: -80px;
  width: 320px;
  height: 320px;
  background: rgba(157, 123, 240, 0.18);
}

.page-glow--two {
  top: 160px;
  right: -100px;
  width: 280px;
  height: 280px;
  background: rgba(122, 73, 226, 0.12);
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 14px 20px 0;
}

.topbar-inner {
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  padding: 0;
}

.brand-block {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-logo {
  width: 54px;
  height: 54px;
  object-fit: contain;
  display: block;
  flex: 0 0 auto;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.brand-name {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  font-family:
    "PingFang SC",
    "Microsoft YaHei",
    "Noto Sans SC",
    "Helvetica Neue",
    Arial,
    sans-serif;
}

.brand-note {
  margin-top: 3px;
  font-size: 13px;
  color: var(--text-secondary);
}

.station-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.station-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 8px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--text-secondary);
  background: transparent;
  border: none;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.station-link:hover {
  color: var(--brand-primary);
  opacity: 0.9;
}

.station-link--current {
  color: var(--brand-primary);
}

.page-main {
  max-width: 1240px;
  margin: 0 auto;
  padding: 14px 20px 34px;
}

.container {
  max-width: 940px;
  margin: 0 auto;
  padding: 4px 0 0;
}

.tabs-nav {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
}

.tab {
  width: 100%;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  cursor: pointer;
  padding: 16px 18px;
  border-radius: 20px;
  background: var(--bg-card-muted);
  border: 1px solid rgba(122, 73, 226, 0.08);
  color: var(--text-primary);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.tab:hover {
  transform: translateY(-2px);
  border-color: var(--border-strong);
  box-shadow: 0 14px 26px rgba(122, 73, 226, 0.08);
}

.tab.active {
  background: linear-gradient(135deg, rgba(122, 73, 226, 0.12) 0%, rgba(157, 123, 240, 0.18) 100%);
  border-color: rgba(122, 73, 226, 0.18);
  box-shadow: 0 14px 28px rgba(122, 73, 226, 0.1);
}

.tab-label {
  font-size: 17px;
  font-weight: 700;
  font-family:
    "PingFang SC",
    "Microsoft YaHei",
    "Noto Sans SC",
    "Helvetica Neue",
    Arial,
    sans-serif;
}

.tab-note {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.6;
  color: var(--text-secondary);
  font-family:
    "PingFang SC",
    "Microsoft YaHei",
    "Noto Sans SC",
    "Helvetica Neue",
    Arial,
    sans-serif;
}

.tool-view {
  max-width: 760px;
  margin: 0 auto;
}

.header {
  text-align: center;
  margin-bottom: 24px;
}

h1 {
  margin: 0 0 20px;
  color: var(--text-primary);
  font-size: 32px;
  font-weight: 600;
  line-height: 1.3;
  font-family:
    "Songti SC",
    "STSong",
    "Noto Serif SC",
    "Source Han Serif SC",
    serif;
}

.control-panel {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-end;
  gap: 14px 18px;
  margin-bottom: 15px;
}

.control-panel--single {
  justify-content: center;
}

.control-panel--compact {
  gap: 12px 16px;
}

.control-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.control-group--select {
  min-width: 160px;
}

.control-group--wide {
  width: min(100%, 340px);
}

.control-label {
  padding-left: 4px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--text-secondary);
}

.stepper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 48px;
  padding: 4px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--bg-card-strong);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.stepper--compact {
  min-height: 44px;
}

.stepper-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--brand-primary);
  background: var(--brand-primary-ghost);
  transition: background 0.2s ease, transform 0.2s ease, color 0.2s ease;
}

.stepper-btn:hover {
  background: var(--brand-primary-ghost-strong);
  transform: translateY(-1px);
}

.stepper-btn:active {
  transform: translateY(0);
}

.stepper-value {
  width: 88px;
  min-height: 40px;
  border: none;
  padding: 0 10px;
  text-align: center;
  background: transparent;
  color: var(--text-primary);
  font-size: 19px;
  font-weight: 700;
  line-height: 1;
  appearance: textfield;
  -moz-appearance: textfield;
}

.month-select {
  width: 100%;
  min-height: 48px;
  padding: 0 42px 0 14px;
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  background: var(--bg-card-strong);
  color: var(--text-primary);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
  appearance: none;
  -webkit-appearance: none;
}

.stepper-value--compact {
  width: 78px;
  font-size: 18px;
}

.control-reset {
  min-height: 42px;
  padding: 0 14px;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  color: var(--brand-primary);
  background: var(--brand-primary-ghost);
  font-size: 14px;
  font-weight: 700;
  transition: background 0.2s ease, transform 0.2s ease;
}

.control-reset:hover {
  background: var(--brand-primary-ghost-strong);
  transform: translateY(-1px);
}

.select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 160px;
}

.select-wrap::after {
  content: "";
  position: absolute;
  top: 50%;
  right: 16px;
  width: 10px;
  height: 10px;
  border-right: 1.8px solid var(--text-secondary);
  border-bottom: 1.8px solid var(--text-secondary);
  transform: translateY(-65%) rotate(45deg);
  pointer-events: none;
}

.select-wrap--compact {
  min-width: 120px;
}

.select-wrap--wide {
  width: 100%;
}

.inline-icon {
  width: 18px;
  height: 18px;
  display: block;
}

.summary {
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.8;
}

.sub-text {
  margin-left: 8px;
  font-size: 13px;
  color: var(--text-muted);
}

.sub-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  padding: 6px;
  margin-bottom: 20px;
  border-radius: 18px;
  background: var(--brand-primary-ghost);
}

.sub-tab {
  min-height: 46px;
  padding: 10px 16px;
  border-radius: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  user-select: none;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.sub-tab:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.54);
}

.sub-tab.active {
  background: #fff;
  color: var(--brand-primary);
  font-weight: 700;
  box-shadow: 0 10px 18px rgba(122, 73, 226, 0.1);
}

.sub-tab-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  display: block;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 0 auto;
  max-width: 450px;
  aspect-ratio: 1 / 1;
}

.grid-cell {
  border: 2px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  padding: 6px 2px;
  text-align: center;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.grid-cell:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  z-index: 2;
}

.cell-palace-tags {
  font-size: 0.65rem;
  opacity: 0.55;
  line-height: 1;
}

.cell-bagua {
  font-size: 0.75rem;
  font-weight: bold;
  opacity: 0.8;
  margin-top: 2px;
  padding: 2px 10px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.4);
}

.cell-title {
  font-size: 1.15rem;
  font-weight: 900;
  letter-spacing: 1px;
}

.cell-main-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.cell-star-num {
  font-size: 1.3rem;
  font-weight: bold;
  line-height: 1;
}

.cell-star-name {
  font-size: 0.8rem;
  font-weight: bold;
  opacity: 0.9;
}

.cell-star-tags {
  font-size: 0.7rem;
  opacity: 0.75;
  white-space: pre-wrap;
  line-height: 1.2;
}

.star-type-water-1 { background-color: #EBF2F7; border-color: #DDE8F0; color: #2F4D66; }
.star-type-water-1 .cell-star-num { color: #1C334A; }
.star-type-earth-2 { background-color: #EAE8E3; border-color: #DFDBD3; color: #5C554B; }
.star-type-earth-2 .cell-star-num { color: #3A352F; }
.star-type-wood-3 { background-color: #F1F5EB; border-color: #E2EAD8; color: #4A6B53; }
.star-type-wood-3 .cell-star-num { color: #324D39; }
.star-type-wood-4 { background-color: #EDF3EC; border-color: #DCE5D9; color: #527A58; }
.star-type-wood-4 .cell-star-num { color: #3C5C41; }
.star-type-earth-5 { background-color: #FBF2E3; border-color: #F2E3CA; color: #B23A2B; }
.star-type-earth-5 .cell-star-num { color: #9A2617; }
.star-type-metal-6 { background-color: #F2F3F4; border-color: #E4E7E9; color: #636D73; }
.star-type-metal-6 .cell-star-num { color: #4A545C; }
.star-type-metal-7 { background-color: #F8EDED; border-color: #F0E2E2; color: #9E5656; }
.star-type-metal-7 .cell-star-num { color: #823E3E; }
.star-type-earth-8 { background-color: #FCF8EE; border-color: #F5ECD4; color: #8C6A3C; }
.star-type-earth-8 .cell-star-num { color: #A67530; }
.star-type-fire-9 { background-color: #F4EEF5; border-color: #E8DCEB; color: #6D4C75; }
.star-type-fire-9 .cell-star-num { color: #51305A; }

.watermark-box {
  text-align: center;
  margin-top: 15px;
  margin-bottom: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0.35;
  user-select: none;
  pointer-events: none;
}

.watermark-logo {
  font-family: "STKaiti", "KaiTi", "楷体", serif;
  font-size: 1.2rem;
  letter-spacing: 4px;
  font-weight: bold;
  color: #8C2218;
}

.distinction-note {
  background: rgba(122, 73, 226, 0.05);
  border: 1px solid rgba(122, 73, 226, 0.12);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 20px;
  font-size: 0.92rem;
  line-height: 1.75;
  color: #555;
}

.distinction-note p {
  margin: 0 0 6px;
}

.distinction-note p:last-child {
  margin-bottom: 0;
}

.sub-hint {
  font-size: 0.82rem;
  color: #999;
  font-style: italic;
}

.advice-section {
  margin-top: 28px;
  padding: 24px 22px 8px;
  border: 1px solid var(--border-soft);
  border-radius: 24px;
  background: rgba(250, 248, 254, 0.84);
}

.advice-section h3 {
  margin-top: 0;
  color: var(--text-primary);
}

.advice-list {
  line-height: 1.85;
  color: #555;
  padding-left: 20px;
}

.warning {
  color: #9A2617;
  font-weight: bold;
}

.lucky {
  color: #A67530;
  font-weight: bold;
}

.zb-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin: 0 auto;
  max-width: 550px;
}

.zb-card {
  border: 2px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  padding: 14px 6px;
  text-align: center;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  gap: 4px;
}

.zb-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  z-index: 2;
}

.zb-month {
  font-size: 0.72rem;
  opacity: 0.65;
  line-height: 1;
}

.zb-star-num {
  font-size: 2.4rem;
  font-weight: bold;
  line-height: 1.1;
}

.zb-star-name {
  font-size: 0.75rem;
  font-weight: bold;
  opacity: 0.9;
}

.zb-label {
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.5px;
}

.lbl-lucky  { color: #A67530; }
.lbl-warn   { color: #823E3E; }
.lbl-danger { color: #9A2617; }

.zb-direction {
  font-size: 0.68rem;
  opacity: 0.6;
  line-height: 1;
}

.site-footer {
  padding: 0 20px 20px;
}

.site-footer-inner {
  max-width: 940px;
  margin: 0 auto;
  text-align: center;
  color: var(--text-secondary);
}

.footer-quote {
  font-size: 13px;
  letter-spacing: 0.16em;
  color: var(--text-primary);
}

.footer-meta {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.8;
}

.footer-meta a {
  color: inherit;
}

.footer-meta a:hover {
  color: var(--brand-primary);
}

.site-records {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 16px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--text-secondary);
}

.site-records a {
  color: inherit;
}

.site-records a:hover {
  color: var(--brand-primary);
}

@media (max-width: 980px) {
  .container {
    padding: 6px 0 0;
  }

  .tabs-nav {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .topbar {
    padding: 12px 12px 0;
  }

  .site-footer {
    padding: 0 12px 18px;
  }

  .footer-quote {
    letter-spacing: 0.1em;
  }

  .site-records {
    padding-top: 4px;
  }

  .page-main {
    padding: 10px 12px 26px;
  }

  .container {
    padding: 4px 0 0;
  }

  .brand-name {
    font-size: 19px;
  }

  .brand-note {
    font-size: 13px;
  }

  h1 {
    font-size: 26px;
  }

  .tab-label {
    font-size: 16px;
  }

  .sub-tabs {
    width: 100%;
    justify-content: center;
  }

  .control-panel {
    gap: 12px;
  }

  .control-group,
  .control-group--select,
  .control-group--wide {
    width: 100%;
    max-width: 320px;
  }

  .stepper,
  .select-wrap {
    width: 100%;
  }

  .stepper-value {
    width: 100%;
    min-width: 0;
  }

  .sub-tab {
    flex: 1 1 0;
    justify-content: center;
  }

  .grid-container {
    max-width: 100%;
    gap: 6px;
  }

  .cell-title {
    font-size: 0.95rem;
  }

  .cell-star-num {
    font-size: 1.1rem;
  }

  .cell-star-name {
    font-size: 0.75rem;
  }

  .cell-star-tags,
  .cell-palace-tags {
    font-size: 0.6rem;
  }

  .zb-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .zb-card {
    padding: 10px 5px;
  }

  .zb-star-num {
    font-size: 2rem;
  }
}
</style>
